import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Bot, Cpu, Network, BarChart3, MessageSquare, Database } from 'lucide-react';

const AIAgents = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const agents = [
    {
      icon: Bot,
      title: 'OpenClaw Agents',
      description: 'Autonomous AI agents that handle complex sales workflows, qualification, and customer engagement without human intervention.',
      color: '#FF6600'
    },
    {
      icon: MessageSquare,
      title: 'Conversational AI',
      description: 'Natural language processing that understands context, intent, and sentiment to deliver human-like customer interactions.',
      color: '#FF8C42'
    },
    {
      icon: BarChart3,
      title: 'Sales Analytics',
      description: 'Real-time insights and predictive analytics that identify opportunities, forecast revenue, and optimize sales strategies.',
      color: '#FFA85C'
    },
    {
      icon: Network,
      title: 'Multi-Channel Integration',
      description: 'Seamlessly connect across email, chat, CRM, and phone systems to provide unified customer experiences.',
      color: '#FF6600'
    },
    {
      icon: Cpu,
      title: 'Local Processing',
      description: 'All AI computations run on your infrastructure with Nvidia DGX Spark and Mac Studios - no cloud dependencies.',
      color: '#FF8C42'
    },
    {
      icon: Database,
      title: 'Custom Training',
      description: 'Train AI models on your proprietary data to understand your industry, products, and unique business processes.',
      color: '#FFA85C'
    }
  ];

  return (
    <section id="agents" ref={sectionRef} className="relative py-24 md:py-32 bg-gradient-to-b from-black via-[#0a0a0a] to-black overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-[#FF6600] rounded-full mix-blend-multiply filter blur-3xl opacity-10"></div>
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-[#FF8C42] rounded-full mix-blend-multiply filter blur-3xl opacity-10"></div>
      </div>

      <div className="relative z-10 container mx-auto px-6">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div
            className={`inline-flex items-center space-x-2 bg-[#FF6600]/10 border border-[#FF6600]/30 rounded-full px-5 py-2 mb-6 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <Bot className="w-4 h-4 text-[#FF6600]" />
            <span className="text-[#FF6600] font-semibold text-sm">AI-Powered Automation</span>
          </div>
          <h2
            className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-6 transition-all duration-700 delay-100 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            Meet Your <span className="text-gradient-orange">AI Sales Team</span>
          </h2>
          <p
            className={`text-lg md:text-xl text-gray-400 transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            OpenClaw Agents work 24/7 to prospect, qualify, and close deals while your human team focuses on high-value strategic relationships.
          </p>
        </div>

        {/* Hero Image */}
        <div
          className={`max-w-5xl mx-auto mb-16 transition-all duration-700 delay-300 ${
            isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}
        >
          <div className="relative rounded-2xl overflow-hidden border border-[#FF6600]/20 shadow-2xl">
            <img
              src="https://images.unsplash.com/photo-1716436329836-208bea5a55e6"
              alt="AI Technology"
              className="w-full h-[400px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">Next-Generation AI Architecture</h3>
              <p className="text-gray-300">Built on cutting-edge LLM technology with enterprise-grade security</p>
            </div>
          </div>
        </div>

        {/* Agent Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {agents.map((agent, index) => {
            const Icon = agent.icon;
            return (
              <Card
                key={index}
                className={`glass-morphism border-white/10 hover-lift transition-all duration-700 group cursor-pointer ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${400 + index * 100}ms` }}
              >
                <CardContent className="p-6">
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-all duration-300 group-hover:scale-110"
                    style={{ backgroundColor: `${agent.color}20` }}
                  >
                    <Icon className="w-7 h-7" style={{ color: agent.color }} />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#FF6600] transition-colors duration-300">
                    {agent.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">{agent.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div
          className={`mt-16 text-center transition-all duration-700 delay-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <p className="text-gray-400 text-lg mb-4">
            Ready to scale your sales team with AI?
          </p>
          <button
            onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            className="text-[#FF6600] hover:text-[#FF8C42] font-semibold text-lg underline underline-offset-4 transition-colors duration-200"
          >
            Schedule a Demo →
          </button>
        </div>
      </div>
    </section>
  );
};

export default AIAgents;
